import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useMyAgents } from '../../lib/my-agents';
import { useTranslation } from '../../lib/i18n';
import { useBasePath } from '../../lib/use-base-path';
import { useLeaveChatOnNavigate } from '../../lib/use-leave-chat';
import { useGenericBi } from '../../lib/use-generic-bi';
import { biPageName } from '../../lib/bi-page-name';
import {
  agentsIslandClickComponent,
  agentsIslandClickFields,
  type AgentsIslandPillState,
} from '../../lib/agents-island-click-bi';
import { AgentDot } from '../AgentDot';
import { MaestroMark } from './MaestroMark';
import { usePiledUpJobs, inProgressJobs } from '../../lib/piled-up-jobs';
import { useRegisterPillEl } from './FlyToPill';
import { useChipSlot } from './chip-morph/ChipSlotContext';
import { useChatOverlayOpen } from '../../lib/use-chat-overlay';
import { useMaestroWorking } from '../../lib/maestro-working';
import { useAgentName } from '../../lib/use-agent-name';
import { useIslandState } from '../../lib/use-island-state';
import { useIslandAgentStarted } from '../../lib/use-island-agent-started';
import { useIslandQuickView } from '../../lib/use-island-quickview';
import { AgentsIslandQuickView, type IslandSide } from './AgentsIslandQuickView';
import styles from './AgentsIslandBadge.module.css';

// The team-summary pill (native's AgentsIslandBadge): overlapping agent dots +
// "N Agents" + a sound-wave divider + "N Skills", in a soft-green capsule. Counts
// come from the one my-agents source (agents + team-wide skills). It rides the
// header on every internal page — Account included, per the desktop Figma
// (13523-270580) — with AppFrame owning that gating; this component just renders
// the pill.
//
// Tapping the pill navigates to Your agents — the team it summarizes — mirroring
// native's AgentsIslandBadge (Android `AgentsIslandNavigationTest`, iOS
// `AgentsIslandBadgeTests.tappingIslandRoutesToAgents`). It never shows ON the
// /agents pages (AppFrame gates it), so the tap is always a real jump.

const MAX_AVATARS = 4;
// Mobile is the canonical Agents-badge green: fill = native `accentGreenBg` light (#E3F8D8 →
// --sym-accent-bg = --ds-green-neon-100), border = native `accentGreen` (#A4FF70 → --sym-accent
// = --ds-green-neon-500). Ink is primary text on the soft-green surface (--sym-ink). These match
// the Symphony Figma "Agents Status Badge" (node 10295-97545); routed through the shared tokens
// so the pill reads identically across platforms and follows the design system.
const PILL_BG = 'var(--sym-accent-bg)';
// The edge is Border/Accent/Default, not the brand FILL token. Identical (#A4FF70) in light; in dark
// the border token is #3D7321 where the fill token stays neon, and #3D7321 is what the dark frame
// draws over this pill's #14250B (Figma 2209:6869, sampled — Monday 12880538148). The blue and orange
// states already read their own `--island-*-border` roles, which is why only the green one moves.
const PILL_BORDER = 'var(--ds-border-accent-default)';
/** The pill's side inset. The quick-view header reuses it (`PANEL_PAD`) — a difference shifts the
 * header's text the moment the panel opens, so the two only ever move together. Figma 13523-215388. */
const ISLAND_PAD_X = 20;

const INK = 'var(--sym-ink)';

// A tiny animated audio-equalizer divider between the Agents and Skills counts:
// a row of thin rounded bars whose heights trace a SINGLE continuous sine wave
// that travels smoothly across the bars at a constant speed, looping forever.
// Shared 1:1 with native's `SoundWaveDivider` (iOS `TimelineView`, Android
// `InfiniteTransition`) — same 4 bars, 2.5×2 spacing, 14pt box, 0.30 min height,
// 1.1s linear loop, ink color. Ported here with a rAF loop that mutates the bar
// heights via refs (no per-frame React re-render). Decorative only.
const SW_BAR_COUNT = 4;
const SW_BAR_WIDTH = 2.5;
const SW_BAR_SPACING = 2;
const SW_BOX_HEIGHT = 14;
const SW_MIN_FRACTION = 0.3;
const SW_PERIOD_MS = 1100;
const SW_PHASE_STEP = (2 * Math.PI) / SW_BAR_COUNT;

// Height of bar `i` at wave `phase`: a sine sampled at a fixed per-bar offset,
// mapped from minFraction..1 of the box height (matches native's `height(for:)`).
function swBarHeight(i: number, phase: number): number {
  const wave = (Math.sin(i * SW_PHASE_STEP + phase) + 1) / 2; // 0…1
  return SW_BOX_HEIGHT * (SW_MIN_FRACTION + (1 - SW_MIN_FRACTION) * wave);
}

export function SoundWave({ active, color = INK }: { active: boolean; color?: string }) {
  const bars = useRef<(HTMLSpanElement | null)[]>([]);
  useEffect(() => {
    // scaleY instead of height — transform runs on the GPU compositor thread,
    // height mutations trigger layout reflow on the main thread every frame.
    const midScale = (SW_MIN_FRACTION + 1) / 2;
    if (!active) {
      for (let i = 0; i < SW_BAR_COUNT; i++) {
        const bar = bars.current[i];
        if (bar) bar.style.transform = `scaleY(${midScale})`;
      }
      return;
    }
    let raf = 0;
    const tick = () => {
      const phase = ((performance.now() % SW_PERIOD_MS) / SW_PERIOD_MS) * 2 * Math.PI;
      for (let i = 0; i < SW_BAR_COUNT; i++) {
        const bar = bars.current[i];
        if (bar) bar.style.transform = `scaleY(${swBarHeight(i, phase) / SW_BOX_HEIGHT})`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: SW_BAR_SPACING,
        height: SW_BOX_HEIGHT,
        flexShrink: 0,
      }}
    >
      {Array.from({ length: SW_BAR_COUNT }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            bars.current[i] = el;
          }}
          style={{
            width: SW_BAR_WIDTH,
            height: SW_BOX_HEIGHT,
            borderRadius: 999,
            background: color,
            transformOrigin: 'center',
          }}
        />
      ))}
    </span>
  );
}

// Number in the display face, label in the UI face.
const NUM_STYLE: React.CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontWeight: 'var(--sym-weight-semibold)',
  fontSize: 20,
  lineHeight: '24px',
  letterSpacing: '-0.4px',
  fontVariationSettings: '"wdth" 100',
  fontFeatureSettings: '"dlig" 1',
  color: INK,
};
const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontWeight: 'var(--sym-weight-semibold)',
  fontSize: 12,
  lineHeight: '12px',
  letterSpacing: '-0.36px',
  color: INK,
};

function Count({ value, label, color }: { value: number; label: string; color?: string }) {
  const num = color ? { ...NUM_STYLE, color } : NUM_STYLE;
  const lbl = color ? { ...LABEL_STYLE, color } : LABEL_STYLE;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={num}>{value}</span>
      <span style={lbl}>{label}</span>
    </span>
  );
}

// The overlapping agent dots + the state count — the pill's inner content. Shared by the pill itself AND
// the quick-view's header row, so when the pill morphs open it IS the panel's header (one continuous
// surface), and the two can never drift apart.
//
// ONE line, at every width. The task's agent and status belong to the quick-view, which is where both
// phones put them.
export function IslandPillContent({
  agentIds,
  count,
  label,
  ink,
  bg,
  announcement,
  beat = false,
}: {
  /** One id per leading glyph: an agent's dot, or `''` for the Maestro / agent-less bucket, which draws
   *  the Maestro mark (mirrors `AgentsIslandQuickViewRow`: `task.agentId ? <AgentDot/> : <MaestroMark/>`). */
  agentIds: string[];
  count: number;
  label: string;
  ink: string;
  bg: string;
  /** The transient "{agent} started working" sentence, which REPLACES the count + label for its window. */
  announcement?: string;
  /** The "working" heartbeat on each cluster glyph — ONLY the blue pill passes it; the header stays static. */
  beat?: boolean;
}) {
  const shown = Math.min(agentIds.length, MAX_AVATARS);
  // ALWAYS one line, every width — the pill names a count and the detail lives in the quick-view, which is
  // what both phones do and what the desktop Figma (13523-270580) draws.
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0,
        minWidth: 0,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
          {agentIds.slice(0, MAX_AVATARS).map((id, i) => (
            <span
              key={id || 'maestro'}
              className={beat ? styles.heartbeat : undefined}
              style={{
                // -6 overlap (≈14px of each 20px avatar visible), matching the Figma working-island spacing.
                marginInlineStart: i === 0 ? 0 : -6,
                display: 'inline-flex',
                // Negative delay so the cluster is already phase-shifted at load; one glyph beats alone (delay 0).
                animationDelay: beat ? `${-(i / shown) * 1.7}s` : undefined,
              }}
            >
              {id ? (
                <AgentDot id={id} size={20} ringColor={bg} />
              ) : (
                // Clip the Maestro tile to a circle so it reads as an avatar the same shape + size as the
                // agent dots (the tile stays square everywhere else it appears).
                <span style={{ display: 'inline-flex', borderRadius: '50%', overflow: 'hidden' }}>
                  <MaestroMark size={20} />
                </span>
              )}
            </span>
          ))}
        </span>
        {/* Shrinkable, so the announcement SENTENCE can ellipsize inside a header that has run out of room
            rather than pushing the pill over the actions beside it. */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {announcement ? (
            <span style={{ ...LABEL_STYLE, fontSize: 14, lineHeight: '18px', color: ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {announcement}
            </span>
          ) : (
            <Count value={count} label={label} color={ink} />
          )}
        </span>
      </span>
    </span>
  );
}

/**
 * Whether this browser has a pill to show at all — the team resolved and is
 * non-empty. `AppFrame` combines it with the route gate to tell the shared chip
 * slot (lib/chip-morph.ts) whether a pill is what the capsule should settle
 * into when a call ends.
 */
export function useAgentsPillReady(): boolean {
  return useMyAgents().status === 'ready';
}

/**
 * `side` says which way the quick-view opens, and it is DECLARED by the mount rather than measured, because
 * measuring gets it wrong here: native infers it from the pill's midpoint across a phone screen, where the
 * pill always sits at an edge, but web's top-bar pin is inset by the content gutter and can land mid-viewport
 * on a wide window — reading as "centre" while plainly being the right-hand island. Each mount knows the
 * answer for certain: the home header is at the inline START (`start` — left, or right in RTL), the fullscreen
 * chat header is centred, the top-bar pin is on the right (the default).
 */
export function AgentsIslandBadge({
  side = 'right',
  onOpenStatus,
  statusOpen = false,
}: {
  side?: IslandSide;
  /** In a phone-width chat whose conversation created something, a tap in every state opens the chat's status
   *  (`ChatStatusIsland`) instead of Your agents or the task quick view. */
  onOpenStatus?: () => void;
  statusOpen?: boolean;
} = {}) {
  const { t } = useTranslation();
  const router = useRouter();
  const state = useMyAgents();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const piledJobs = usePiledUpJobs();
  const agentsActive = piledJobs !== null && inProgressJobs(piledJobs).length > 0;
  const registerPill = useRegisterPillEl();
  // The pill shares one slot with the minimized call (lib/chip-morph.ts): it
  // steps aside while a call is shrunk, and stays laid out but unpainted for the
  // length of the morph, so the capsule the user sees is the overlay's.
  const slot = useChipSlot('pill');
  // The three-state resolution — green resting / blue in-motion / orange waiting — driven by the one
  // task-count source both this pill and its quick-view share. Called unconditionally (before the early
  // returns) so hook order is stable.
  const island = useIslandState();
  // The transient "{agent} started working" name (5s), or null. Announced when the GLOBAL in-motion count
  // rises — see lib/island-agent-started.ts.
  const agentStarted = useIslandAgentStarted();
  // The Maestro mid-turn in the user's own chat. TRANSIENT and never persisted — it is not a task and
  // not an outreach, and if the turn produces either, those appear on their own. Suppressed while the
  // chat is open, where the transcript is already the indicator.
  const maestro = useMaestroWorking(useChatOverlayOpen());
  // The user's own name for the assistant — the working sentence is written with the token, not a
  // hardcoded "Maestro". See `maestroText` below.
  const agentName = useAgentName();
  const quickView = useIslandQuickView();
  // Blue/orange tap opens the quick-view popover anchored to the pill; `pillEl` is the anchor element.
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [pillEl, setPillEl] = useState<HTMLSpanElement | null>(null);

  // When the island resolves back to the resting green pill, the quick-view has nothing left to describe —
  // close it. The green branch below already unmounts the card via an early return, so it vanishes instantly
  // (no collapse curve, matching the immediate dismiss chosen for native); this also clears `quickViewOpen`
  // so the card can't silently re-pop the next time the island goes blue. Mirrors iOS `islandResolvedRegular`
  // and Android's regular-close LaunchedEffect.
  // Only when NO blue/announcement overlay is up: `isBlue` also fires on `maestro.working` and
  // `agentStarted`, so without excluding them the auto-close would dismiss the Maestro-working (or
  // agent-started) quick-view the instant it opened, even though the island has not returned to green.
  const islandResolvedRegular = island.color === 'green' && !agentStarted && !maestro.working;
  useEffect(() => {
    if (islandResolvedRegular && quickViewOpen) setQuickViewOpen(false);
  }, [islandResolvedRegular, quickViewOpen]);

  // No pill until the team resolves, and none for an empty roster — a "0 Agents"
  // flash would read as broken.
  // The pill paints only once BOTH loads have resolved — the roster AND the account-wide quick-view list
  // `island` (`useIslandState`) itself reads its colour and counts off. Gating on a DIFFERENT query than
  // the one `island` is actually sourced from is exactly the bug this used to have: it gated on the old
  // per-workspace aggregate while `useIslandState` had already moved onto `useIslandQuickView`, so the
  // aggregate could settle first and paint a green resting pill on real counts that were still loading —
  // the very flash this gate exists to prevent. Native waits for both for the same reason (it holds a
  // spinner; web reserves the slot and paints nothing, which is what it already did while the roster
  // loaded).
  //
  // `useAgentsPillReady` deliberately does NOT include this: AppFrame calls it on every route to answer
  // "does a pill exist here" for the chip slot, so gating it on the quick-view would fetch it on
  // routes with no island at all. Slot eligibility is a ROUTE question; this is a paint question.
  // An empty team still shows ("0 Agents · 0 Skills"), as on both phones: the empty state and onboarding
  // are when the island matters most. Only an unresolved roster paints nothing, so no wrong "0" flashes.
  if (state.status !== 'ready') return null;
  if (quickView.status !== 'ready') return null;
  if (!slot.mounted) return null;
  const { agents, skills } = state.team;

  // pageName = the CURRENT screen (pill mounts on many routes); component_name names the action;
  // pill colour + counts ride additionalFields. Shared contract with native AgentsIslandClickBi.
  const pageName = biPageName(router.pathname, router.query);
  // The announcement outranks the colour here too, matching native's `AgentsIslandClickBi` — a tap during the
  // window is reported as `agent_started`, not as the colour underneath it.
  // The RENDERED state, in the pill's own precedence — not the colour underneath it. `maestroWorking`
  // paints the blue chip while `island.color` is usually still green (the Maestro's turn is not a task,
  // so it moves no count), and reporting that green was a tap on a pill nobody saw.
  const pillState: AgentsIslandPillState = agentStarted
    ? 'agent_started'
    : maestro.working
    ? 'maestro_working'
    : island.color === 'green'
    ? 'green'
    : island.color === 'blue'
    ? 'in_progress'
    : 'waiting';
  const clickExtra = agentsIslandClickFields({
    pillState,
    inMotionCount: island.inMotionCount,
    // TASKS, deliberately: `waiting_count` is a shared 1:1 contract and the pill's own number is now a
    // CARD count, which rides beside it as `waiting_questions`.
    waitingCount: island.waitingTaskCount,
    // Task-borne question CARDS only: the outreach-only cards and the open asks are in the pill's number but not in
    // this field, which the phones report off the aggregate's `waitingQuestionsTotal` (it excludes them too).
    waitingQuestions: island.waitingQuestionCards,
    agentCount: agents.length,
    skillsCount: skills.length,
  });
  const logIslandClick = (opensQuickView: boolean) => {
    const button = agentsIslandClickComponent(opensQuickView);
    logPageClickBi(
      { page_name: pageName, button_name: button, origin: 'header' },
      { componentName: button, additionalFields: clickExtra },
    );
  };

  const wrapperRef = (el: HTMLSpanElement | null) => {
    registerPill(el);
    slot.ref(el);
    setPillEl(el);
  };
  const wrapperStyle: React.CSSProperties = { display: 'inline-flex', visibility: slot.painted ? undefined : 'hidden' };
  const onTap = (e: React.MouseEvent) => {
    if (onOpenStatus) {
      e.preventDefault();
      onOpenStatus();
      return;
    }
    leaveChat();
    logIslandClick(false);
  };

  // GREEN — the resting pill: agents · skills. Frozen from the original; tap navigates to Your Agents,
  // matching native's tap contract (Android `AgentsIslandNavigationTest`, iOS
  // `AgentsIslandBadgeTests.tappingIslandRoutesToAgents`).
  if (island.color === 'green' && !agentStarted) {
    return (
      <span ref={wrapperRef} data-testid="agents-island-badge" style={wrapperStyle}>
        <Link
          href="/agents"
          as={`${basePath}/agents`}
          aria-label={t('island.aria', { agents: agents.length, skills: skills.length })}
          {...(onOpenStatus ? { 'aria-haspopup': 'dialog' as const, 'aria-expanded': statusOpen } : {})}
          onClick={onTap}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: `8px ${ISLAND_PAD_X}px`,
            borderRadius: 999,
            background: PILL_BG,
            border: `1px solid ${PILL_BORDER}`,
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            cursor: 'pointer',
            color: INK,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {agents.slice(0, MAX_AVATARS).map((a, i) => (
              <span key={a.id} style={{ marginInlineStart: i === 0 ? 0 : -14, display: 'inline-flex' }}>
                <AgentDot id={a.id} size={20} ringColor={PILL_BG} />
              </span>
            ))}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Count value={agents.length} label={t(agents.length === 1 ? 'island.agent' : 'island.agents')} />
            <SoundWave active={agentsActive} />
            <Count value={skills.length} label={t(skills.length === 1 ? 'island.skill' : 'island.skills')} />
          </span>
        </Link>
      </span>
    );
  }

  // BLUE (in motion) / ORANGE (waiting on you). Colours come from the shared status-badge tokens so the
  // pill matches the badge each state wears elsewhere (never a literal). Blue carries the orange PIP when
  // something is ALSO waiting — the co-signal, so a pending decision is never hidden behind "in motion".
  // Reuses the app's established status vocabulary ("In progress" / "Action Needed") rather than new i18n
  // keys. Tap still routes to Your Agents in this phase; the quick-view overlay replaces it in Phase 3.
  const isBlue = island.color === 'blue' || !!agentStarted || maestro.working;
  // The state colour is the pill's OUTLINE only (Figma `Border/In progress/Default` #3B6BE1 /
  // `Border/Panding/Default` #BE4300). Its TEXT is `Text & Icon/Primary` — black — on all three states, which
  // is also what both phones do (`IslandColors.coloredInk`). Painting the text in the state colour is what
  // made web's blue and orange pills disagree with the design.
  // The island's OWN roles, not the generic badge tokens: a badge fill LIGHTENS in dark
  // (#E1E9FB → #A0B8F2) while the ink on it flips to near-white. The phones carry a dedicated
  // `island*` set for exactly this reason; these mirror it. See the island block in globals.css.
  const edge = isBlue ? 'var(--island-in-progress-border)' : 'var(--island-waiting-border)';
  const bg = isBlue ? 'var(--island-in-progress-bg)' : 'var(--island-waiting-bg)';
  // BLUE counts AGENTS at work — the distinct agents behind the live runs — not tasks: the state now
  // means "agents whose loop is executing right now" on all three surfaces. Orange keeps its task count.
  const count = isBlue ? island.workingAgents : island.waitingCount;
  // The pill's LEADING GLYPHS: the actual working / action-needed agents (their dots, `''` → the Maestro
  // mark), mirroring what the phones now get from the server aggregate — not the generic roster cluster.
  // Fall back to the roster while the id list is empty (loading, or an announcement-only blue with no
  // counted work), so the pill is never glyph-less. `''` resolves to the Maestro mark inside the content.
  const rosterIds = agents.map((a) => a.id);
  // The agent-started FLASH names ONE agent, so its pill shows that one agent's own dot as a SINGLE glyph —
  // the started agent's real id, or `''` (the Maestro mark) for an agent-less start — never the general
  // working cluster or the roster (which would show several dots beside a sentence naming one agent, or the
  // stale roster before the id list lands). Highest precedence, mirroring native's agent-started pill
  // (`agentIds: [agentStartedAgentId ?? ""]`). The Maestro's OWN turn is next (['' ] — the Maestro bucket).
  const workingIds = agentStarted
    ? [agentStarted.agentId]
    : maestro.working
    ? ['']
    : island.workingAgentIds.length > 0
    ? island.workingAgentIds
    : rosterIds;
  const waitingIds = island.waitingAgentIds.length > 0 ? island.waitingAgentIds : rosterIds;
  const pillAgentIds = isBlue ? workingIds : waitingIds;
  // The name a single working agent's announcement uses: the assistant's own name for the Maestro bucket
  // (`''`), else the roster agent's name by id (assistant name if it can't be resolved).
  const resolveWorkingName = (id: string): string =>
    id === '' ? agentName.name : agents.find((a) => a.id === id)?.name ?? agentName.name;
  // The island's OWN vocabulary, shared with the phones (`agentsIsland.*`) — not the task-status words.
  // Singular follows the pill's existing count+label precedent (`island.agent` / `island.agents`).
  //
  // ⚠️ The orange state says "Waiting on you" and NOT "Action needed", and that is the whole point of it.
  // This pill is an ACCOUNT-WIDE roll-up: it counts every space, ignores the Tasks screen's Space and Agent
  // filters, and on the phones it adds inbound outreaches the task list does not hold. The Tasks screen's
  // own "Action needed" tab counts the FILTERED list under it. Both numbers are right for what they count,
  // and while they wore the same words the screen showed "17 Action needed" floating over a tab reading
  // "Action needed 6" — two numbers claiming to be the same number. The island cannot know the screen's
  // filter, so the two can never be made equal; naming them apart is the fix, and the blue state was
  // already doing it ("agents at work" names its noun rather than a task status).
  //
  // No count branch on the orange side: "Waiting on you" reads the same at 1 as at 17, and the singular
  // used to reach into `agentTasks.tab.waiting` — the island literally rendering the TAB's string, which is
  // how the two labels were guaranteed identical in the first place.
  const label = isBlue
    ? t(island.workingAgents === 1 ? 'agentsIsland.agentAtWork' : 'agentsIsland.agentsAtWork')
    : t('agentsIsland.waiting');
  // The transient announcement OVERLAYS the colour states for its 5s window (precedence
  // actionCompleted > agentStarted > blue > orange > green, native `islandPillState`). It wears the BLUE
  // chip whatever the underlying state — it is an agent-working announcement — and carries NO live dot: the
  // dot means "this loop is running right now", which the sentence is already saying.
  const startedText = agentStarted ? t('agentsIsland.agentStarted', { agentName: agentStarted.name }) : undefined;
  // The Maestro's own turn sits just under the announcement in precedence: an explicit agent-started
  // announcement is the more specific thing to say, and both wear the same blue chip. Unlike the
  // counted states this carries no number — one turn is not a count, and "1" would read as a task.
  // The assistant is RENAMEABLE, so the sentence takes the user's own chosen name rather than baking in
  // "The Maestro" — which is what all 37 locales used to do ("Le Maestro travaille", "Der Maestro
  // arbeitet"), so a user who named theirs Aria still read about the Maestro. `agent.name` is the FULL
  // name as set, article included, which is why the copy never prepends one of its own (that would render
  // "the The Maestro"). Same token, same sentence, same rule as both phones.
  const maestroText = !startedText && maestro.working ? t('agentsIsland.maestroWorking', { maestroAgentName: agentName.name }) : undefined;
  // Blue with exactly ONE working agent reads as a named sentence rather than a "1 agent at work" count —
  // "{name} is working", the assistant's name for the Maestro bucket. Two or more keep the count + label.
  // Lowest of the announcement tiers: an explicit agent-started or a live Maestro turn is the more
  // specific thing to say.
  const workingSingleText =
    !startedText && !maestroText && isBlue && island.workingAgentIds.length === 1
      ? t('agentsIsland.workingSingle', { agentName: resolveWorkingName(island.workingAgentIds[0]) })
      : undefined;
  const announcementText = startedText ?? maestroText ?? workingSingleText;

  // Tap opens the quick-view popover anchored to the pill; the `<Link href="/agents">` stays as the
  // no-JS fallback (progressive enhancement) and keeps this a styled anchor, not a raw button. Gated on
  // `slot.painted` so it can't open mid-morph against a hidden anchor.
  const openQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!slot.painted) return;
    if (onOpenStatus) {
      onOpenStatus();
      return;
    }
    logIslandClick(true);
    setQuickViewOpen((v) => !v);
  };

  // The pill — ONE return for blue and orange (they must not drift). BLUE (in-motion) wears the reference
  // "working" animation: the whole pill breathes, a conic arc travels the 2px ring, a shimmer sweeps the
  // fill and the avatar cluster beats — all off under prefers-reduced-motion (AgentsIslandBadge.module.css).
  // ORANGE/green stay static. The quick-view header is ALWAYS static (no `beat`, no overlays) so the open
  // card reads as a settled surface.
  const animate = isBlue;
  // The pill anchor (Link). While animating, the edge is an INSET shadow (not a border) so the conic ring
  // shows in the 2px gap, and the shimmer sweep sits above the fill and below the zIndex:1 content.
  const pill = (
    <Link
      href="/agents"
      as={`${basePath}/agents`}
      aria-label={announcementText ?? `${count} ${label}`}
      aria-haspopup="dialog"
      aria-expanded={onOpenStatus ? statusOpen : quickViewOpen}
      onClick={openQuickView}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: `8px ${ISLAND_PAD_X}px`,
        borderRadius: 999,
        overflow: 'hidden',
        background: bg,
        ...(animate ? { boxShadow: `inset 0 0 0 1px ${edge}` } : { border: `1px solid ${edge}` }),
        textDecoration: 'none',
        cursor: 'pointer',
        color: INK,
        // When animating, the OUTER breath wrapper carries the open-state hide; otherwise the pill does.
        ...(animate ? {} : { visibility: quickViewOpen ? 'hidden' : undefined }),
      }}
    >
      {animate ? <span className={styles.sweep} aria-hidden /> : null}
      <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
        <IslandPillContent
          agentIds={pillAgentIds}
          count={count}
          label={label}
          ink={INK}
          bg={bg}
          announcement={announcementText}
          beat={animate}
        />
      </span>
    </Link>
  );

  return (
    <span ref={wrapperRef} data-testid="agents-island-badge" style={wrapperStyle}>
      {animate ? (
        // OUTER ring wrapper: clips the conic to a rounded rect leaving a 2px band for it to peek through,
        // carries the whole-island breath, and hides while the quick-view stands in for the pill. It is an
        // INNER span (not the wrapperRef anchor), so the breath scale never moves the quick-view anchor rect.
        <span
          className={styles.breath}
          style={{
            position: 'relative',
            display: 'inline-flex',
            borderRadius: 999,
            overflow: 'hidden',
            padding: 2,
            visibility: quickViewOpen ? 'hidden' : undefined,
          }}
        >
          <span className={styles.conic} aria-hidden />
          {pill}
        </span>
      ) : (
        pill
      )}
      <AgentsIslandQuickView
        anchorEl={pillEl}
        open={quickViewOpen}
        tone={isBlue ? 'blue' : 'orange'}
        side={side}
        headerContent={
          // The header is a STILL copy of the pill and reads identically — including the single-agent
          // "{name} is working" / maestro sentences — so it carries the same `announcement` (no `beat`,
          // so it stays static). Absent announcement (≥2 agents) → the big count + label, like the pill.
          <IslandPillContent agentIds={pillAgentIds} count={count} label={label} ink={INK} bg={bg} announcement={announcementText} />
        }
        onClose={() => setQuickViewOpen(false)}
      />
    </span>
  );
}
